/**
 * AI 编辑助手 - 核心模块
 * ============================================
 *
 * 职责:
 *   1. 全局配置(模型 ID / 路由提示词)
 *   2. 可选取区域注册表(配合 v-ai-selectable 指令)
 *   3. @资产 提供者注册(页面把自己的 characterList/sceneList/propList 暴露给侧边栏)
 *   4. 组装发给后端 LLM 的 content(路由提示词 + 选区上下文 + 资产 + 对话历史 + 用户消息)
 *   5. 调用后端推理接口(流式失败不自动重发，避免重复执行和扣费)
 *   6. 解析模型按协议返回的 JSON({intent, reply, edits})
 *
 * 与现有逻辑的关系:
 *   - 完全复用 /api/c/scripts/reasoning(同 158 角色检索 / 157 AI补分镜的调用方式)
 *   - 不直接改任何页面数据;"应用修改"通过页面注册选区时提供的 apply() 回调执行,
 *     由页面自己复用既有保存链路(如 updateScriptScene / _persistGrokPromptAndClearDownstream)
 */

import Vue from 'vue';
import { reasonScript, reasonScriptStreamBySSE } from '@/api/c/scripts';

/* ============================================
 * 配置
 * ============================================ */

export const AI_ASSISTANT_CONFIG = {
  /** 后端 ai_models.id —— 121 多模态编辑 */
  MODEL_ID: 121,

  /** 使用 SSE 流式接口；请求结果不明时禁止自动重发 */
  USE_STREAM: true,

  /** 本地 Mock 模式默认值(也可不改代码,用 localStorage.setItem('ai_sidebar_mock','1') 临时开启) */
  MOCK_MODE: false,

  /** 注入对话历史的最大轮数(user+assistant 各算一条) */
  MAX_HISTORY_MESSAGES: 8,

  /** 一次请求最多携带的图片 URL 数 */
  MAX_IMAGES: 6,

  /**
   * 选取模式下「任意图片直接拾取」的最小渲染尺寸(px):
   * 小于该宽高的图片(图标/箭头等)不会被识别为可选图片,避免误选。
   */
  PICK_IMAGE_MIN_SIZE: 36,

  /**
   * 发送时尽力把图片字节打包进请求(images_base64 字段):
   * 浏览器 fetch 图片 URL → 压缩为 JPEG → base64。
   * 仅在图片 CDN 开启 CORS 时可成功;失败静默回退 URL 方式(images 字段),不影响发送。
   */
  IMAGE_FETCH_TIMEOUT: 6000,      // 单图下载超时(ms)
  IMAGE_BASE64_MAX_EDGE: 1280,    // 压缩后最长边(px)
  IMAGE_BASE64_QUALITY: 0.85,     // JPEG 质量
  IMAGE_BASE64_TOTAL_MAX: 5000000, // 一次请求 base64 字符总量上限(约 3.7MB 二进制)

  /**
   * 是否在请求 content 里携带路由系统指令。
   * 【已置为 true】路由指令随请求下发,前端在 content 顶部携带 ROUTING_PROMPT,
   * 协议本体 + 动态上下文(当前页面/选中区域/引用资产/选中图片/用户消息)一起发给模型。
   * 确保模型一定收到 JSON 协议(否则只会返回纯文本聊天,intent 永远是 qa,图像编辑/写回都不触发)。
   * 待后端「模型管理 → 121 → 系统提示词」粘贴同一份 ROUTING_PROMPT 后,可改回 false 省流量。
   */
  SEND_ROUTING_PROMPT: true,

  /**
   * 路由系统提示词(唯一正本):
   * 现配置在后台模型 121 的「系统提示词」字段——粘贴下方 join 后的文本即可,
   * 注意只粘贴到"8. reply 一律使用简体中文。"为止,不要把【当前页面】等动态上下文贴进去。
   * 由它负责"识别用户意图"并约束输出协议。修改协议时:改这里 + 同步更新后台配置。
   */
  ROUTING_PROMPT: [
    '你是「漫剧创作平台」内置的 AI 编辑助手,负责帮用户查看、改写、优化页面中的内容:',
    '分镜脚本、视频画面提示词(画面描述)、角色/场景/道具的设定描述,以及分析用户选中的图片。',
    '',
    '先根据【用户消息】与上下文判断用户意图 intent,然后严格按以下 JSON 协议输出。',
    '只输出一个 JSON 对象,不要输出协议之外的任何文字,不要用 Markdown 代码块包裹:',
    '',
    '{"intent":"edit | qa | generate | edit_image","reply":"给用户的简短中文说明(1~3句)","edits":[{"region":"R1","content":"改写后的完整内容"}],"image_edit":{"image":"<图片URL>","instruction":"<图像编辑指令>","references":["<可选的其他参考图URL>"]}}',
    '其中 image_edit 字段仅在 intent=edit_image 时输出,其余 intent 必须省略该字段。',
    '',
    '规则:',
    '1. intent=edit:用户要求修改/优化/改写某个选区时使用。edits 里对目标选区给出"完整的替换后文本"(不是 diff、不是片段),region 填选区编号(如 R1)。可同时修改多个选区,但只能针对【选中区域】里"可写回=是"的选区。',
    '2. intent=qa:用户在提问/分析/讨论(含分析图片)时使用。edits 必须为 []。回答写在 reply,此时 reply 可以较长。',
    '3. intent=generate:用户要求生成新内容但没有可写回的目标选区时使用。生成结果直接写在 reply,edits 为 []。',
    '4. 改"画面描述/视频提示词"类内容:保留原有镜头语言结构与结尾约束句式(如"约束:严格参考...保持无字幕..."等必须保留),正文默认控制在 120~250 字,除非用户另有要求。',
    '5. 改"分镜脚本"类内容:保留【景别·机位】、(场景)、(角色)[服装] 等既有标记格式。',
    '6. 用户 @ 了资产时,资产的名称与设定是硬约束:人物外观、场景氛围必须与资产设定一致。',
    '7. 选区原文中的 asset://xxx 等素材 ID 标记必须在改写结果中原样保留。',
    '8. reply 一律使用简体中文。',
    '9. intent=edit_image:用户要求对某张选中图片本身进行修改/重绘/局部调整(如"把背景换成雨夜""去掉床头的台灯""让她闭上眼睛")时使用。image_edit.image 必须从【选中图片】或【选中区域】附带图片中已列出的 URL 里原样复制一个,不得编造;instruction 是给图像编辑模型的中文祈使指令,需独立可执行、具体明确、80 字内;其余参考图放 references(可省略)。此时 edits 必须为 [],reply 用一句话说明将执行的编辑。上下文中没有任何图片 URL 时禁止使用该 intent,改用 qa 在 reply 中请用户先选取图片。',
  ].join('\n'),
};

/* ============================================
 * 事件总线(侧边栏 <-> 页面)
 * ============================================ */

export const aiBus = new Vue();

/** 打开侧边栏(可携带初始上下文) */
export function openAiSidebar(payload) {
  aiBus.$emit('ai-sidebar:open', payload || {});
}

/* ============================================
 * 选区注册表
 * ============================================ */

let _regionSeq = 0;
/** Map<HTMLElement, { id, el, options }> */
const _regionRegistry = new Map();

function _normalizeOptions(value) {
  if (value == null) return {};
  if (typeof value === 'string') return { label: value };
  if (typeof value === 'function') return value() || {};
  return value;
}

export function registerAiRegion(el, value) {
  if (!el || _regionRegistry.has(el)) return;
  _regionSeq += 1;
  _regionRegistry.set(el, { id: 'region_' + _regionSeq, el, options: value });
  el.setAttribute('data-ai-selectable', '1');
}

export function updateAiRegion(el, value) {
  const entry = _regionRegistry.get(el);
  if (entry) entry.options = value;
}

export function unregisterAiRegion(el) {
  if (!el) return;
  _regionRegistry.delete(el);
  try { el.removeAttribute('data-ai-selectable'); } catch (e) { /* el 可能已脱离文档 */ }
}

/**
 * 把注册项解析成快照:{ id, el, type, label, text, image, canApply, apply }
 * - 每次解析都重新读取 options(指令 componentUpdated 会刷新),保证拿到最新值
 */
export function resolveAiRegion(el, childKey = null) {
  const entry = _regionRegistry.get(el);
  if (!entry) return null;
  let opt = _normalizeOptions(entry.options);
  if (opt.disabled) return null;
  if (childKey !== null) {
    opt = (opt.children || []).find((child, index) => String(child.key || index) === String(childKey));
    if (!opt || opt.disabled) return null;
  }

  let text = null;
  try {
    if (typeof opt.getText === 'function') text = opt.getText();
    else if (typeof opt.text === 'string') text = opt.text;
    else if (!opt.image && !opt.getImage) text = el.innerText; // 兜底:直接取元素文本
  } catch (e) { console.warn('[aiAssistant] getText 失败:', e); }
  if (typeof text === 'string') {
    text = text.trim();
  } else {
    text = null;
  }

  let image = null;
  try {
    if (typeof opt.getImage === 'function') image = opt.getImage();
    else if (typeof opt.image === 'string') image = opt.image;
  } catch (e) { console.warn('[aiAssistant] getImage 失败:', e); }
  if (typeof image !== 'string' || !image) image = null;

  const apply = typeof opt.apply === 'function' ? opt.apply : null;
  // 可选的图片写回入口:选区来源若支持(如角色/场景/道具卡),AI 生成的新图可一键替换原图
  const applyImage = typeof opt.applyImage === 'function' ? opt.applyImage : null;
  // 可选的「数据级写回标识」(纯可序列化对象,如 {kind:'role',scriptId,id,name}):
  // 来源页面销毁/刷新后仍可凭它直接调保存接口完成替换,跨页面不失效
  const applyTarget = (opt.applyTarget && typeof opt.applyTarget === 'object') ? opt.applyTarget : null;
  // 可选的子选区列表(多镜头场景):每个子项有独立的 label/getText/apply 回调
  const children = Array.isArray(opt.children) ? opt.children : null;

  return {
    id: opt.key || (entry.id + (childKey === null ? '' : ':' + childKey)),
    persistent: opt.persistent === true && !!opt.key,
    el,
    childKey,
    type: opt.type || 'text',
    label: opt.label || '未命名区域',
    text,
    image,
    canApply: !!(apply || opt.canApply), // canApply=true 而 apply=null 时表示有子选区可写(children),父级本身不可直接写回
    apply,
    canApplyImage: !!applyImage,
    applyImage,
    applyTarget,
    children,
  };
}

/** DOM 可被 Vue 复用于其他片段；仅刷新仍指向同一数据项的选区。 */
export function refreshAiRegion(snapshot) {
  if (!snapshot) return null;
  const fresh = snapshot.el && resolveAiRegion(snapshot.el, snapshot.childKey == null ? null : snapshot.childKey);
  if (fresh && fresh.id === snapshot.regionId) return fresh;
  // 仅页面明确声明的业务身份允许跨 DOM 或刷新恢复，临时注册编号不可复用。
  if (!snapshot.persistent || !snapshot.regionId) return null;
  for (const el of _regionRegistry.keys()) {
    const parent = resolveAiRegion(el);
    if (!parent) continue;
    if (parent.persistent && parent.id === snapshot.regionId) return parent;
    for (const [index, child] of (parent.children || []).entries()) {
      if (!child.persistent || child.key !== snapshot.regionId) continue;
      const region = resolveAiRegion(el, child.key || index);
      if (region && region.persistent && region.id === snapshot.regionId) return region;
    }
  }
  return null;
}

/** 从坐标点向上查找最近的已注册选区元素 */
export function findAiRegionFromPoint(x, y) {
  let node = document.elementFromPoint(x, y);
  while (node && node !== document.body && node !== document.documentElement) {
    if (_regionRegistry.has(node)) return resolveAiRegion(node);
    node = node.parentElement;
  }
  return null;
}

/** 当前是否还注册着该元素(用于发送前刷新选区快照) */
export function isAiRegionAlive(el) {
  return _regionRegistry.has(el);
}

/**
 * 扫描所有已注册选区，找到 getImage() 与给定 URL 匹配的选区，
 * 通过 Vue 组件实例获取 scriptId 和角色 ID 构建写回目标。
 * 不依赖 options.applyTarget（旧版页面代码可能未传该字段）。
 */
export function findApplyTargetByImage(imageUrl) {
  if (!imageUrl) return null;
  const norm = (u) => String(u || '').split('?')[0];
  const target = norm(imageUrl);
  for (const [el, entry] of _regionRegistry) {
    const opt = _normalizeOptions(entry.options);
    // 优先使用 options 中的 applyTarget
    if (opt.applyTarget && opt.applyTarget.scriptId && opt.applyTarget.id) {
      let img = null;
      try {
        if (typeof opt.getImage === 'function') img = opt.getImage();
        else if (typeof opt.image === 'string') img = opt.image;
      } catch (e) { continue; }
      if (img && norm(img) === target) return opt.applyTarget;
    }
  }
  // 回退：通过 Vue 实例查找
  return _findTargetViaVue(imageUrl, target);
}

/** 回退方案：通过 Vue 组件实例查找角色/场景/道具 ID */
function _findTargetViaVue(imageUrl, normTarget) {
  if (!normTarget) normTarget = String(imageUrl || '').split('?')[0];
  console.warn('[findTarget:vue] 开始查找, normTarget:', normTarget.substring(0, 80));
  // 遍历注册表找到匹配的选区元素
  let matchedEl = null;
  let matchedType = null;
  let registrySize = 0;
  for (const [el, entry] of _regionRegistry) {
    registrySize++;
    const opt = _normalizeOptions(entry.options);
    let img = null;
    try {
      if (typeof opt.getImage === 'function') img = opt.getImage();
      else if (typeof opt.image === 'string') img = opt.image;
    } catch (e) { continue; }
    const imgNorm = img ? String(img).split('?')[0] : '';
    if (img && imgNorm === normTarget) {
      matchedEl = el;
      matchedType = opt.type;
      console.warn('[findTarget:vue] 匹配选区, type:', opt.type, 'label:', opt.label, 'img:', imgNorm.substring(0, 60));
      break;
    }
  }
  console.warn('[findTarget:vue] 注册表大小:', registrySize, '匹配:', !!matchedEl);
  if (!matchedEl) return null;
  // 向上查找 Vue 组件实例（先看当前元素，再向上遍历 DOM 树）
  let vm = matchedEl.__vue__;
  if (!vm) {
    let parent = matchedEl.parentElement;
    let maxWalk = 30;
    while (parent && maxWalk-- > 0) {
      if (parent.__vue__) { vm = parent.__vue__; break; }
      parent = parent.parentElement;
    }
  }
  console.warn('[findTarget:vue] __vue__:', !!vm, vm ? vm.$options.name || '匿名组件' : 'null');
  let maxDepth = 15;
  while (vm && maxDepth-- > 0) {
    const sid = vm.scriptId;
    // 兼容 edit.vue 的 characterList 和 scenes.vue 的 roleList
    const charList = vm.characterList || vm.roleList || [];
    const hasChars = charList.length > 0;
    console.warn('[findTarget:vue] 组件:', vm.$options.name || '匿名', 'scriptId:', sid, 'chars:', hasChars ? charList.length : 'N/A');
    if (sid && hasChars) {
      for (const c of charList) {
        if (!c || c.id === '__narrator__') continue;
        const avatarNorm = String(c.avatar || c.imageUrl || '').split('?')[0];
        if (avatarNorm === normTarget) {
          const name = c.name || c.displayName || '';
          console.warn('[findTarget:vue] 找到角色!', c.id, name);
          return { kind: 'role', scriptId: sid, id: c.id, name, defaultFormId: (c.forms && c.forms[0] && c.forms[0].id) || null };
        }
      }
    }
    if (sid) {
      // 尝试在 sceneList 中查找
      const scenes = vm.sceneList || [];
      for (const s of scenes) {
        if (!s) continue;
        const sImg = String(s.imageUrl || s.thumbnail || s.stampedImageUrl || '').split('?')[0];
        if (sImg && sImg === normTarget) {
          return { kind: 'scene', scriptId: sid, id: s.id, name: s.name || s.displayName || '' };
        }
      }
      // 尝试在 propList 中查找
      const props = vm.propList || [];
      for (const p of props) {
        if (!p) continue;
        const pImg = String(p.imageUrl || p.thumbnail || p.stampedImageUrl || '').split('?')[0];
        if (pImg && pImg === normTarget) {
          return { kind: 'prop', scriptId: sid, id: p.id, name: p.name || '' };
        }
      }
    }
    vm = vm.$parent;
  }
  console.warn('[findTarget:vue] 未找到匹配的角色/场景/道具');
  return null;
}

/* ============================================
 * 任意图片直接拾取(无需注册)
 * ============================================
 * 选取模式下,坐标处若没有命中已注册选区,则回退为「图片拾取」:
 * 页面上任意 <img>(或带 background-image 的元素)都可以被点选,
 * 其图片 URL 会作为独立图片 chip 填入对话上下文,随请求发给多模态模型。
 */

/** 还原 CDN 缩略图为原图 URL(剥掉 buildThumbUrl 追加的 imageView2 处理参数) */
export function restoreOriginalImageUrl(url) {
  if (!url || typeof url !== 'string') return url || '';
  if (url.indexOf('qiniufast.meidawenhua.com') === -1 && url.indexOf('ai.cdn.meidawenhua.com') === -1) return url;
  const qIdx = url.indexOf('?');
  if (qIdx === -1) return url;
  const base = url.slice(0, qIdx);
  const rest = url
    .slice(qIdx + 1)
    .split('&')
    .filter((p) => p && p.indexOf('imageView2') !== 0);
  return rest.length ? base + '?' + rest.join('&') : base;
}

function _bgImageUrl(el) {
  try {
    const bg = window.getComputedStyle(el).backgroundImage;
    if (!bg || bg === 'none') return '';
    const m = /url\((['"]?)(.*?)\1\)/.exec(bg);
    return m ? m[2] : '';
  } catch (e) { return ''; }
}

function _pickLabelOf(el, fallback) {
  const t = ((el.getAttribute && (el.getAttribute('alt') || el.getAttribute('title') || el.getAttribute('aria-label'))) || '').trim();
  return t || fallback;
}

/**
 * 从坐标点拾取「可选图片」(已注册选区优先级更高,由调用方先查 findAiRegionFromPoint)。
 * 利用 elementsFromPoint 自上而下的整叠元素,穿透角标/遮罩等覆盖层命中底下的 <img>;
 * 同时兼容用 background-image 渲染的图块。
 * @returns {null | { el, url, label, isLocal }}  isLocal: blob:/data: 等模型无法访问的本地资源
 */
export function findPickableImageFromPoint(x, y) {
  const minSize = AI_ASSISTANT_CONFIG.PICK_IMAGE_MIN_SIZE;
  let stack = [];
  try {
    stack = document.elementsFromPoint(x, y) || [];
  } catch (e) {
    const single = document.elementFromPoint(x, y);
    if (single) stack = [single];
  }

  const _result = (el, rawUrl, fallbackLabel) => {
    const url = restoreOriginalImageUrl(rawUrl);
    return {
      el,
      url,
      label: _pickLabelOf(el, fallbackLabel),
      isLocal: !/^(https?:|\/\/)/i.test(url),
    };
  };

  for (let i = 0; i < stack.length && i < 12; i++) {
    const el = stack[i];
    if (!el || el === document.documentElement || el === document.body) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width < minSize || rect.height < minSize) continue;

    if (el.tagName === 'IMG') {
      const src = el.currentSrc || el.src || '';
      if (!src) continue;
      return _result(el, src, '页面图片');
    }
    const bg = _bgImageUrl(el);
    if (bg) return _result(el, bg, '页面图片');
  }
  return null;
}

/* ============================================
 * 图片字节打包(images_base64)
 * ============================================
 * 发送前尽力把每张图片的字节取回并压缩为 base64,随请求附带,
 * 让后端可以零取图直接喂给多模态模型。
 * 受浏览器同源策略限制:仅当图片 CDN 返回 CORS 头时可成功;
 * 失败(跨域/超时/解码失败)一律返回 null 并静默回退 URL 方式。
 */

/** Map<url, {mime,data}|null> 会话级缓存:同一张图重复发送不再下载/编码 */
const _imgB64Cache = new Map();

function _blobToJpegBase64(blob) {
  return new Promise((resolve, reject) => {
    const objUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const maxEdge = AI_ASSISTANT_CONFIG.IMAGE_BASE64_MAX_EDGE;
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (!w || !h) throw new Error('图片尺寸为 0');
        const scale = Math.min(1, maxEdge / Math.max(w, h));
        w = Math.max(1, Math.round(w * scale));
        h = Math.max(1, Math.round(h * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff'; // 透明 PNG 转 JPEG 时垫白底
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', AI_ASSISTANT_CONFIG.IMAGE_BASE64_QUALITY);
        resolve({ mime: 'image/jpeg', data: dataUrl.slice(dataUrl.indexOf(',') + 1) });
      } catch (e) {
        reject(e);
      } finally {
        URL.revokeObjectURL(objUrl);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objUrl);
      reject(new Error('图片解码失败'));
    };
    img.src = objUrl;
  });
}

/** 下载并压缩单张图片;失败返回 null(典型原因:CDN 未开 CORS) */
export async function fetchImageAsBase64(url) {
  if (!url || !/^https?:/i.test(url)) return null;
  if (_imgB64Cache.has(url)) return _imgB64Cache.get(url);
  let out = null;
  try {
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), AI_ASSISTANT_CONFIG.IMAGE_FETCH_TIMEOUT) : null;
    const resp = await fetch(url, { mode: 'cors', signal: ctrl ? ctrl.signal : undefined });
    if (timer) clearTimeout(timer);
    if (!resp.ok) throw new Error('HTTP ' + resp.status);
    const blob = await resp.blob();
    out = await _blobToJpegBase64(blob);
  } catch (e) {
    console.warn('[aiAssistant] 图片转 base64 失败(多为 CDN 未开 CORS,已回退 URL 方式):', url, e && e.message);
    out = null;
  }
  _imgB64Cache.set(url, out);
  return out;
}

/* ============================================
 * @资产 提供者
 * ============================================ */

/** Set<fn> ; fn() => Array<{type:'char'|'scene'|'prop', id, name, avatar, sub, description}> */
const _assetProviders = new Set();

export function registerAiAssetProvider(fn) {
  if (typeof fn === 'function') _assetProviders.add(fn);
}

export function unregisterAiAssetProvider(fn) {
  _assetProviders.delete(fn);
}

/** 汇总所有页面提供的资产(按 type+id 去重) */
/* ============================================
 * 图片写回目标解析器(跨页面/历史卡片兜底)
 * ============================================
 * 页面(如角色和场景页)注册一个解析器:给定图片 URL,返回该图所属资产的写回目标
 * { kind:'role'|'scene'|'prop', scriptId, id, name }。
 * 当任务卡片自身未携带 applyTarget(历史卡片、跨页面选取等)时,「应用替换」用它兜底。
 */
const _applyTargetResolvers = new Set();
export function registerImageApplyTargetResolver(fn) {
  if (typeof fn === 'function') _applyTargetResolvers.add(fn);
}
export function unregisterImageApplyTargetResolver(fn) {
  _applyTargetResolvers.delete(fn);
}
/** 用已注册解析器按图片 URL 反查写回目标;查不到返回 null */
export function resolveImageApplyTarget(imageUrl) {
  if (!imageUrl) return null;
  for (const fn of _applyTargetResolvers) {
    try {
      const t = fn(imageUrl);
      if (t && t.scriptId && t.id) return t;
    } catch (e) { console.warn('[aiAssistant] 写回目标解析失败:', e); }
  }
  return null;
}

export function collectAiAssets() {
  const out = [];
  const seen = new Set();
  _assetProviders.forEach((fn) => {
    let list = [];
    try { list = fn() || []; } catch (e) { console.warn('[aiAssistant] 资产提供者执行失败:', e); }
    list.forEach((a) => {
      if (!a || !a.name) return;
      const key = (a.type || 'asset') + ':' + (a.id != null ? a.id : a.name);
      if (seen.has(key)) return;
      seen.add(key);
      out.push({
        type: a.type || 'asset',
        id: a.id != null ? a.id : key,
        name: a.name,
        avatar: a.avatar || '',
        sub: a.sub || '',
        description: a.description || a.sub || '',
      });
    });
  });
  return out;
}

export const AI_ASSET_TYPE_LABEL = { char: '角色', scene: '场景', prop: '道具', asset: '资产' };

/* ============================================
 * content 组装
 * ============================================ */

const REGION_TYPE_LABEL = {
  script: '分镜脚本',
  video_prompt: '视频画面提示词',
  storyboard: '分镜内容',
  field: '表单字段',
  image: '图片',
  'asset-char': '角色资产',
  'asset-scene': '场景资产',
  'asset-prop': '道具资产',
  text: '文本',
};

/**
 * @param {Object} p
 * @param {string} p.userText
 * @param {Array}  p.regions  选区快照数组(发送前已刷新),每项含 refKey(R1/R2...)
 * @param {Array}  p.images   独立图片数组 [{ refKey, label, url }]
 * @param {Array}  p.assets   @资产 [{ type, name, description, avatar }]
 * @param {Array}  p.history  [{ role:'user'|'assistant', text }]
 * @param {string} p.pagePath
 */
export function buildAssistantContent(p) {
  const lines = [];
  // 路由指令默认由后台模型「系统提示词」下发,前端仅发动态上下文;SEND_ROUTING_PROMPT=true 时回退旧行为
  if (AI_ASSISTANT_CONFIG.SEND_ROUTING_PROMPT) {
    lines.push('【路由系统指令】');
    lines.push(AI_ASSISTANT_CONFIG.ROUTING_PROMPT);
    lines.push('');
  }
  lines.push('【当前页面】' + (p.pagePath || '未知'));

  const history = (p.history || []).slice(-AI_ASSISTANT_CONFIG.MAX_HISTORY_MESSAGES);
  if (history.length) {
    lines.push('');
    lines.push('【对话历史】(最近 ' + history.length + ' 条,仅供理解语境)');
    history.forEach((m) => {
      const t = (m.text || '').slice(0, 800);
      lines.push((m.role === 'user' ? '用户: ' : '助手: ') + t.replace(/\n/g, ' '));
    });
  }

  const regions = p.regions || [];
  if (regions.length) {
    lines.push('');
    lines.push('【选中区域】');
    regions.forEach((r) => {
      lines.push(
        '[' + r.refKey + '] 类型=' + (REGION_TYPE_LABEL[r.type] || r.type) +
        ' 名称=' + r.label +
        ' 可写回=' + (r.canApply ? '是' : '否') +
        (r.image ? ' 附带图片=' + r.image : '')
      );
      if (r.text) {
        lines.push('内容:');
        lines.push('<<<');
        lines.push(r.text);
        lines.push('>>>');
      }
    });
  }

  const images = p.images || [];
  if (images.length) {
    lines.push('');
    lines.push('【选中图片】(图片已随请求一并提供,可直接分析)');
    images.forEach((img) => {
      lines.push('[' + img.refKey + '] 名称=' + (img.label || '图片') + ' URL=' + img.url);
    });
  }

  const assets = p.assets || [];
  if (assets.length) {
    lines.push('');
    lines.push('【引用资产】(创作硬约束)');
    assets.forEach((a, i) => {
      lines.push(
        '[@A' + (i + 1) + '] ' + (AI_ASSET_TYPE_LABEL[a.type] || '资产') + '「' + a.name + '」' +
        (a.description ? ': ' + String(a.description).slice(0, 600) : '') +
        (a.avatar ? ' 图片=' + a.avatar : '')
      );
    });
  }

  lines.push('');
  lines.push('【用户消息】');
  lines.push(p.userText || '');

  return lines.join('\n');
}

/* ============================================
 * LLM 调用
 * ============================================ */

/**
 * 本地 Mock 模式开关:
 *   浏览器控制台执行 localStorage.setItem('ai_sidebar_mock','1') 后刷新即开启;
 *   removeItem('ai_sidebar_mock') 后刷新关闭。
 * 开启后不调用任何后端推理接口(不耗积分),返回符合输出协议的模拟结果,
 * 用于在后端/121 模型未就绪时验证「选取 → 发送 → 流式 → 应用写回」全链路。
 * 注意:Mock 的 edits 带【MOCK改写】前缀,点「应用修改」会真实落库,请在测试项目里验证。
 */
export function isAiMockEnabled() {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('ai_sidebar_mock') === '1') return true;
  } catch (e) { /* 隐私模式等 */ }
  return !!AI_ASSISTANT_CONFIG.MOCK_MODE;
}

async function _mockAssistantCall(p) {
  const content = p.content || '';

  // 从组装好的 content 里反解选区(与 buildAssistantContent 的格式严格对应)
  const regions = [];
  const blockRe = new RegExp('\\[(R\\d+)\\] 类型=(\\S+) 名称=(.*?) 可写回=(是|否)[^\\n]*\\n(?:内容:\\n<<<\\n([\\s\\S]*?)\\n>>>)?', 'g');
  let m;
  while ((m = blockRe.exec(content))) {
    regions.push({ ref: m[1], type: m[2], label: m[3], writable: m[4] === '是', body: (m[5] || '').trim() });
  }
  const userIdx = content.lastIndexOf('【用户消息】');
  const userText = userIdx !== -1 ? content.slice(userIdx + '【用户消息】'.length).trim() : '';
  const imagesCount = (p.images || []).length;

  // 与真实调用同一条代码路径地检测「图片字节打包(images_base64)」:
  // 前端测试阶段即可确认 CDN CORS 是否可用,为后端接入方案(B 服务端取图 / C 字节直达)提前定论
  let b64Note = '';
  const imgUrls = (p.images || []).slice(0, AI_ASSISTANT_CONFIG.MAX_IMAGES);
  if (imgUrls.length) {
    let okCount = 0;
    try {
      const encoded = await Promise.all(imgUrls.map((u) => fetchImageAsBase64(u)));
      okCount = encoded.filter((b) => b && b.data).length;
    } catch (e) { /* 与真实路径一致:打包失败不影响发送 */ }
    b64Note = okCount > 0
      ? '\n[图片字节打包检测] 成功 ' + okCount + '/' + imgUrls.length + ' — CDN CORS 可用,真实调用将携带 images_base64,后端可零取图直接喂模型(交付单方案 C)。'
      : '\n[图片字节打包检测] 成功 0/' + imgUrls.length + ' — CDN 未开 CORS,真实调用仅携带 URL(images 字段),后端需服务端取图(交付单方案 B,推荐)。';
  }

  let obj;
  const writables = regions.filter((r) => r.writable);
  const wantsImageEdit = imgUrls.length > 0 && /改|换成|换个|去掉|移除|删掉|擦除|加上|添加|重绘|修图|P掉|p掉|变成|调亮|调暗/.test(userText);
  if (wantsImageEdit) {
    obj = {
      intent: 'edit_image',
      reply: '(MOCK)已识别为图像编辑意图,将对第 1 张选中图执行:' + userText + '。点「开始编辑」可演练完整流程(MOCK 下不真实调用模型/不扣积分)。' + b64Note,
      edits: [],
      image_edit: {
        image: imgUrls[0],
        instruction: userText,
        references: imgUrls.slice(1),
      },
    };
  } else if (writables.length) {
    obj = {
      intent: 'edit',
      reply: '(MOCK 本地模拟)已按「' + (userText || '默认优化') + '」对 ' + writables.length + ' 个选区生成改写,点「应用修改」可验证写回链路。' + b64Note,
      edits: writables.map((r) => ({
        region: r.ref,
        content: '【MOCK改写 · ' + r.label + '】\n' + (r.body || '(原选区无文本)') + '\n\n(本段为本地模拟输出,未调用真实模型)',
      })),
    };
  } else if (imagesCount) {
    obj = {
      intent: 'qa',
      reply: '(MOCK)已收到 ' + imagesCount + ' 张图片与消息「' + userText + '」。真实图片分析需后端按交付单将图片接入 121 多模态输入后生效。' + b64Note,
      edits: [],
    };
  } else {
    obj = {
      intent: 'qa',
      reply: '(MOCK)收到:' + (userText || '(空消息)') + '\n当前为本地模拟模式(localStorage 中 ai_sidebar_mock=1),移除该项并刷新后将调用真实 121 模型。',
      edits: [],
    };
  }

  // 模拟流式输出
  const json = JSON.stringify(obj);
  if (typeof p.onProgress === 'function') {
    const step = Math.max(8, Math.ceil(json.length / 15));
    for (let i = step; i < json.length + step; i += step) {
      p.onProgress(json.slice(0, i), { mock: true });
      await new Promise((r) => setTimeout(r, 40));
    }
  }
  return json;
}

/**
 * 调用 121 多模态编辑模型。
 * @param {Object} p
 * @param {string}   p.content
 * @param {string[]} p.images     图片 URL 数组(多模态输入)。
 *                                后端 /reasoning 若暂未读取 images 字段,需在后端把它透传给模型;
 *                                content 文本里也带了 URL,后端可二选一接入。
 * @param {string|number} p.scriptId
 * @param {Function} p.onProgress (text, payload) 流式回调
 * @returns {Promise<string>} 模型原始返回文本
 */
export async function callAiAssistant(p) {
  if (isAiMockEnabled()) return _mockAssistantCall(p);

  const data = {
    modelId: AI_ASSISTANT_CONFIG.MODEL_ID,
    content: p.content,
  };
  if (p.scriptId) data.script_id = p.scriptId;
  if (p.images && p.images.length) {
    const urls = p.images.slice(0, AI_ASSISTANT_CONFIG.MAX_IMAGES);
    data.images = urls;
    // best-effort:同时附带图片字节(images_base64),后端可零取图直接喂模型;
    // CDN 未开 CORS 时该数组为空或缺项,后端仍按 images(URL) 兜底。
    try {
      const encoded = await Promise.all(urls.map((u) => fetchImageAsBase64(u)));
      const withData = [];
      let total = 0;
      encoded.forEach((b, i) => {
        if (!b || !b.data) return;
        if (total + b.data.length > AI_ASSISTANT_CONFIG.IMAGE_BASE64_TOTAL_MAX) return; // 超总量上限的丢弃,保留 URL
        total += b.data.length;
        withData.push({ url: urls[i], mime: b.mime, data: b.data });
      });
      if (withData.length) data.images_base64 = withData;
    } catch (e) {
      console.warn('[aiAssistant] images_base64 打包失败,仅按 URL 发送:', e && e.message);
    }
  }

  if (AI_ASSISTANT_CONFIG.USE_STREAM) {
    const res = await reasonScriptStreamBySSE(data, { onProgress: p.onProgress });
    const text = res && res.data && res.data.result;
    if (!text) throw new Error('推理返回为空，未自动重复提交');
    return text;
  }

  const res = await reasonScript(data);
  return (res && res.data && res.data.result) || '';
}

/* ============================================
 * 模型返回解析
 * ============================================ */

/** 从文本中提取第一个配平的 JSON 对象(容忍 ```json 包裹 / 前后多余文字) */
function _extractFirstJsonObject(raw) {
  if (!raw) return null;
  let s = String(raw).replace(/```json/gi, '```').trim();
  // 去掉代码块围栏
  if (s.indexOf('```') !== -1) s = s.replace(/```/g, '\n');
  const start = s.indexOf('{');
  if (start === -1) return null;
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (let i = start; i < s.length; i++) {
    const ch = s[i];
    if (inStr) {
      if (esc) { esc = false; }
      else if (ch === '\\') { esc = true; }
      else if (ch === '"') { inStr = false; }
      continue;
    }
    if (ch === '"') { inStr = true; continue; }
    if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) {
        const candidate = s.slice(start, i + 1);
        try { return JSON.parse(candidate); } catch (e) { return null; }
      }
    }
  }
  return null;
}

function _shotNumber(value) {
  if (/^\d+$/.test(value)) return Number(value);
  const digits = { 零: 0, 〇: 0, 一: 1, 二: 2, 两: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9 };
  const units = { 十: 10, 百: 100, 千: 1000 };
  if (!/[十百千]/.test(value)) return Number([...value].map(char => digits[char]).join(''));
  let total = 0;
  let digit = 0;
  for (const char of value) {
    if (char in digits) digit = digits[char];
    else if (char in units) { total += (digit || 1) * units[char]; digit = 0; }
    else return NaN;
  }
  return total + digit;
}

// 分镜编号不是 R 编号：只选“分镜3”时，它在本次请求中可能是 R1。
function _resolveEditRegion(label, regions) {
  const ref = label.match(/^R\s*([1-9]\d*)$/i);
  if (ref) return 'R' + ref[1];
  const shot = label.match(/^(分镜|镜头|镜号|场景|Scene)\s*([\d零〇一二两三四五六七八九十百千]+)$/i);
  if (!shot) return label;
  const number = _shotNumber(shot[2]);
  const matches = regions.filter(region =>
    [...(region.label || '').matchAll(/(分镜|镜头|镜号|场景|Scene)\s*([\d零〇一二两三四五六七八九十百千]+)/gi)]
      .some(match => match[1].toLowerCase() === shot[1].toLowerCase() && _shotNumber(match[2]) === number));
  return matches.length === 1 && matches[0].canApply ? matches[0].refKey : null;
}

/** 非 JSON 回复兼容 C/D 分镜标题；只能按明确编号或唯一选区名称匹配，不能按顺序猜测。 */
function _extractEditsFromText(text, regions) {
  if (!text || typeof text !== 'string') return [];
  text = text.trim().replace(/^```[^\r\n]*\r?\n([\s\S]*?)\r?\n```$/, '$1');
  const markerRe = /^[ \t]*(?:#{1,6}[ \t]*)?(?:\*\*|__)?[ \t]*[\[【]?((?:R\s*[1-9]\d*|(?:分镜|镜头|镜号|场景|Scene)\s*[\d零〇一二两三四五六七八九十百千]+))(?=[\s\]】:：（(*_]|$)[ \t]*[\]】]?[ \t]*(?:[（(][^)）\r\n]*[)）][ \t]*)?(?:\*\*|__)?[ \t]*[:：]?[ \t]*(?:\*\*|__)?[ \t]*/gim;
  const markers = [];
  let m;
  while ((m = markerRe.exec(text)) !== null) {
    markers.push({ label: m[1].trim(), index: m.index, fullMatch: m[0] });
  }
  if (markers.length < 1) return [];
  // R 标记包住的内容可以包含内层分镜，优先保留整个 R 选区，不拆散其镜头结构。
  const explicit = markers.filter(marker => /^R\s*\d+$/i.test(marker.label));
  const shots = markers.filter(marker => /^分镜/.test(marker.label));
  const selected = explicit.length ? explicit : shots.length ? shots : markers;
  const edits = [];
  for (let i = 0; i < selected.length; i++) {
    const start = selected[i].index + selected[i].fullMatch.length;
    const end = i + 1 < selected.length ? selected[i + 1].index : text.length;
    const content = text.slice(start, end).trim();
    const region = _resolveEditRegion(selected[i].label, regions);
    if (!content || !region || edits.some(edit => edit.region === region)) return [];
    edits.push({ region, content });
  }
  return edits;
}

/**
 * 解析模型输出 → { intent, reply, edits:[{region, content}] }
 * 解析失败时尝试从文本中提取分段编辑建议;若仍无法提取则整体当作 reply(intent=qa)。
 */
export function parseAssistantOutput(raw, regions = []) {
  const trimmedRaw = (raw || '').trim();
  const fallback = { intent: 'qa', reply: trimmedRaw, edits: [], imageEdit: null };
  const obj = _extractFirstJsonObject(raw);
  if (!obj || typeof obj !== 'object') {
    // JSON 解析失败:尝试从纯文本中提取分段编辑建议
    const textEdits = _extractEditsFromText(trimmedRaw, regions);
    if (textEdits.length) {
      return {
        intent: 'edit',
        reply: '已生成 ' + textEdits.length + ' 条修改建议:',
        edits: textEdits,
        imageEdit: null,
      };
    }
    return fallback;
  }

  const intent = typeof obj.intent === 'string' ? obj.intent : 'qa';
  const reply = typeof obj.reply === 'string' ? obj.reply : (typeof obj.answer === 'string' ? obj.answer : '');
  let edits = [];
  if (Array.isArray(obj.edits)) {
    edits = obj.edits
      .filter((e) => e && typeof e === 'object' && typeof e.content === 'string')
      .map((e) => {
        const label = String(e.region || e.regionId || '').trim();
        return { region: _resolveEditRegion(label, regions) || label, content: e.content };
      });
  }
  let imageEdit = null;
  if (intent === 'edit_image' && obj.image_edit && typeof obj.image_edit === 'object') {
    const ie = obj.image_edit;
    const refs = Array.isArray(ie.references) ? ie.references.filter((u) => typeof u === 'string' && u) : [];
    if (typeof ie.instruction === 'string' && ie.instruction.trim()) {
      imageEdit = {
        image: typeof ie.image === 'string' ? ie.image.trim() : '',
        instruction: ie.instruction.trim(),
        references: refs,
      };
    }
  }
  // JSON 解析成功但 edits 为空:尝试从 reply 文本中提取分段编辑建议
  if (!edits.length && (intent === 'qa' || intent === 'generate' || intent === 'edit')) {
    const replySource = reply || trimmedRaw;
    const textEdits = _extractEditsFromText(replySource, regions);
    if (textEdits.length) {
      return {
        intent: 'edit',
        reply: '已生成 ' + textEdits.length + ' 条修改建议:',
        edits: textEdits,
        imageEdit,
      };
    }
  }
  if (!reply && !edits.length && !imageEdit) return fallback;
  return { intent, reply, edits, imageEdit };
}
